import PropTypes from 'prop-types';
import React, { memo } from 'react';
import styled, { css } from 'styled-components';
import { colorVariables } from 'theme/variables';
import lockIcon from 'assets/icons/lock.svg';
import Icon from '../Icon/Icon';
import {
  ButtonStyled,
  ContentWrap,
  StyledExpandedPrice,
  StyledExpandedQuality,
  StyledIcon,
  StyledText,
  Tooltip,
} from './styles/ButtonStyled';

const Button = ({
  icon = null,
  type = null,
  active = false,
  text = null,
  expandedTextPrice = null,
  expandedTextQuality = null,
  textSize = null,
  textType = null,
  textFont = null,
  disable = false,
  size = null,
  align = null,
  iconSize = 24,
  iconFill = 'white',
  iconStroke = null,
  background = null,
  buttonStyle = null,
  isMobile = false,
  textColor = null,
  hoverColor = null,
  fixedTextSize = false,
  separation = null,
  tag = 'button',
  textWeight = '',
  toolTipText = null,
  toolTipBottom = false,
  toolTipRight = false,
  viewBox,
  isShowLockIcon,
  ...props
}) => (
  <ButtonStyled
    {...props}
    as={tag}
    size={size}
    active={active}
    disable={disable}
    isMobile={isMobile}
    buttonStyle={buttonStyle}
    hoverColor={buttonStyle === 'background' ? 'white' : hoverColor}
    type={type}
    style={{
      backgroundColor: background,
    }}
  >
    <ContentWrap align={align}>
      {icon && (
        <StyledIcon
          buttonStyle={buttonStyle}
          isMobile={isMobile}
          disable={disable}
          name={icon}
          fillColor={colorVariables[textColor || iconFill]}
          strokeColor={colorVariables[iconStroke]}
          size={iconSize}
          separation={separation}
          active={active}
          viewBox={viewBox}
        />
      )}
      {text && (
        <StyledText
          tag="span"
          buttonStyle={buttonStyle}
          isMobile={isMobile}
          disable={disable}
          color={colorVariables[textColor]}
          fixedSize={fixedTextSize}
          size={textSize}
          type={textType}
          font={textFont}
          weight={textWeight}
        >
          {text}
        </StyledText>
      )}
      {text && expandedTextQuality && (
        <StyledExpandedQuality>
          <StyledText
            tag="span"
            buttonStyle={buttonStyle}
            isMobile={isMobile}
            disable={disable}
            color={colorVariables[textColor]}
            size={textSize}
            type={textType}
            font={textFont}
            fixedSize={fixedTextSize}
          >
            {expandedTextQuality}
          </StyledText>
        </StyledExpandedQuality>
      )}
      {text && expandedTextPrice && (
        <StyledExpandedPrice>
          <StyledText
            tag="span"
            buttonStyle={buttonStyle}
            isMobile={isMobile}
            disable={disable}
            color={colorVariables[textColor]}
            size={textSize}
            type={textType}
            font={textFont}
            fixedSize={fixedTextSize}
          >
            {expandedTextPrice}
          </StyledText>
        </StyledExpandedPrice>
      )}
      {isShowLockIcon && (
        <StyledLockIcon isMobile={isMobile} isParentDisabled={disable} />
      )}
    </ContentWrap>
    {!text && icon && toolTipText && !isMobile && (
      <Tooltip toolTipBottom={toolTipBottom} toolTipRight={toolTipRight}>
        {toolTipText}
      </Tooltip>
    )}
  </ButtonStyled>
);

Button.propTypes = {
  tag: PropTypes.string,
  icon: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({ id: PropTypes.string }),
  ]),
  text: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
    PropTypes.object,
  ]),
  expandedTextPrice: PropTypes.string,
  expandedTextQuality: PropTypes.string,
  type: PropTypes.string,
  active: PropTypes.bool,
  textColor: PropTypes.string,
  textSize: PropTypes.string,
  textType: PropTypes.string,
  textFont: PropTypes.string,
  hoverColor: PropTypes.string,
  size: PropTypes.string,
  disable: PropTypes.bool,
  align: PropTypes.string,
  iconSize: PropTypes.number,
  iconFill: PropTypes.string,
  iconStroke: PropTypes.string,
  background: PropTypes.string,
  buttonStyle: PropTypes.string,
  isMobile: PropTypes.bool,
  fixedTextSize: PropTypes.bool,
  separation: PropTypes.number,
  textWeight: PropTypes.string,
  tooltipText: PropTypes.string,
  toolTipBottom: PropTypes.bool,
  toolTipRight: PropTypes.bool,
  isShowLockIcon: PropTypes.bool,
};

export default memo(Button);

const StyledLockIcon = styled(Icon).attrs((props) => ({
  name: lockIcon,
  size: props.isMobile ? 10 : 16,
  fillColor: 'white',
  viewBox: '0 0 22 22',
}))`
  position: absolute;
  ${({ isMobile, isParentDisabled }) => css`
    top: ${isMobile ? 19 : 18}px;
    left: ${isMobile ? 19 : 20}px;
    opacity: ${isParentDisabled ? 1 : 0.5};
  `}
`;
